<template>
  <div class="has-padding-bottom">
    <div class="mb-6">
      <ui-base-input-field
        :label="$t('Components.Companies.CreateCompany.Label_CompanyName')"
        v-model.trim="company.Name"
        :error="nameErrors.toString()"
        @input="$v.company.Name.$touch()"
        @blur="$v.company.Name.$touch()"
        required
      />
      <ui-base-input-field
        :label="$t('Components.Companies.CreateCompany.Label_Address')"
        v-model.trim="company.Address"
        :error="addressErrors"
        @input="$v.company.Address.$touch()"
        @blur="$v.company.Address.$touch()"
        required
      />
      <ui-base-input-field
        :label="$t('Components.Companies.CreateCompany.Label_PostalCode')"
        v-model.trim="company.PostalCode"
        :error="postalCodeErrors"
        @input="$v.company.PostalCode.$touch()"
        @blur="$v.company.PostalCode.$touch()"
        required
      />
      <ui-base-input-field
        :label="$t('Components.Companies.CreateCompany.Label_City')"
        v-model.trim="company.City"
        :error="cityErrors"
        @input="$v.company.City.$touch()"
        @blur="$v.company.City.$touch()"
        required
      />
      <BaseFieldTemplate
        :required="false"
        :label="$t('Components.Companies.CreateCompany.Label_Country')"
      >
        <template v-slot:fields>
          <!-- <div class="select">
            <select v-model="company.Country">
              <option :value="0" :selected="Number(company.Country) === 0">
                {{
                  $t('Components.Companies.CreateCompany.Option_ChooseCountry')
                }}
              </option>
              <option
                v-for="(country, key) in $t('CountryLabels')"
                :key="key"
                :selected="key === 'Country' + company.Country"
                :value="Number(key.split('Country')[1])"
              >
                {{ country }}
              </option>
            </select>
          </div> -->

          <div class="select">
            <select v-model="company.Country">
              <option>Select dropdown</option>
              <option
                v-for="country in countries"
                :key="country.Id"
                :value="country.CountryCode.toUpperCase()"
                :selected="
                  company.Country.toUpperCase() ===
                  country.CountryCode.toUpperCase()
                "
              >
                {{ $t('CountryLabels.Country' + country.Id) }}
              </option>
            </select>
          </div>
        </template>
      </BaseFieldTemplate>
    </div>
    <hr />
    <div class="has-margin-bottom">
      <ui-base-input-field
        :label="$t('Components.Companies.CreateCompany.Label_ContactName')"
        v-model.trim="company.ContactName"
        :error="contactNameErrors"
        @input="$v.company.ContactName.$touch()"
        @blur="$v.company.ContactName.$touch()"
        required
      />

      <ui-base-input-field
        :label="$t('Components.Companies.CreateCompany.Label_ContactPhone')"
        v-model.trim="company.ContactPhone"
      />

      <ui-base-input-field
        :label="$t('Components.Companies.CreateCompany.Label_ContactEmail')"
        v-model.trim="company.ContactEmail"
        :error="contactEmailErrors"
        @input="$v.company.ContactEmail.$touch()"
        @blur="$v.company.ContactEmail.$touch()"
        required
      />
    </div>

    <Message
      class="is-danger has-margin-bottom"
      :message="errorMessage"
      v-if="errorMessage"
    />

    <div class="level">
      <div class="level-left"></div>
      <div class="level-right">
        <button
          class="button is-primary"
          @click="save"
          :class="{ 'is-loading': isSaving }"
          :disabled="Boolean($v.$error)"
          v-html="$t('Form.Control.Save')"
        ></button>
      </div>
    </div>
  </div>
</template>

<script>
const Message = () => import('@/components/UI/Message')
const BaseFieldTemplate = () => import('@/components/UI/Form/BaseFieldTemplate')

import { required, email } from 'vuelidate/lib/validators'
import companyProvider from '@/providers/company'
import countryProvider from '@/providers/country'

const touchMap = new WeakMap()

export default {
  name: 'createCompany',

  props: {
    locationId: {
      type: Number,
      required: true,
    },
  },

  components: {
    BaseFieldTemplate,
    Message,
  },

  data() {
    return {
      countries: [],
      errorMessage: '',
      isSaving: false,
      company: {
        LocationId: this.locationId,
        Name: '',
        Address: '',
        PostalCode: '',
        City: '',
        Country: 'NL',
        ContactPhone: '',
        ContactName: '',
        ContactEmail: '',
      },
    }
  },

  /**
   * Form validation rules
   */
  validations() {
    let output = {
      Name: {
        required,
      },
      Address: {
        required,
      },
      PostalCode: {
        required,
      },
      City: {
        required,
      },
      // Country: {
      //   required
      // },
      ContactName: {
        required,
      },
      ContactEmail: {
        required,
        email,
      },
    }
    return { company: output }
  },

  computed: {
    // Error messages
    nameErrors() {
      const errors = []
      if (!this.$v.company.Name.$dirty) {
        return errors
      }
      if (!this.$v.company.Name.required) {
        errors.push(this.$t('Form.InputErrors.Required'))
      }
      return errors
    },

    addressErrors() {
      const errors = []
      if (!this.$v.company.Address.$dirty) {
        return errors
      }
      if (!this.$v.company.Address.required) {
        errors.push(this.$t('Form.InputErrors.Required'))
      }
      return errors
    },

    postalCodeErrors() {
      const errors = []
      if (!this.$v.company.PostalCode.$dirty) {
        return errors
      }
      if (!this.$v.company.PostalCode.required) {
        errors.push(this.$t('Form.InputErrors.Required'))
      }
      return errors
    },

    cityErrors() {
      const errors = []
      if (!this.$v.company.City.$dirty) {
        return errors
      }
      if (!this.$v.company.City.required) {
        errors.push(this.$t('Form.InputErrors.Required'))
      }
      return errors
    },

    countryErrors() {
      const errors = []
      if (!this.$v.company.Country.$dirty) {
        return errors
      }
      if (!this.$v.company.Country.largerThen) {
        errors.push(this.$t('Form.InputErrors.Required'))
      }
      return errors
    },

    contactNameErrors() {
      const errors = []
      if (!this.$v.company.ContactName.$dirty) {
        return errors
      }
      if (!this.$v.company.ContactName.required) {
        errors.push(this.$t('Form.InputErrors.Required'))
      }
      return errors
    },

    contactEmailErrors() {
      const errors = []
      if (!this.$v.company.ContactEmail.$dirty) {
        return errors
      }
      if (!this.$v.company.ContactEmail.required) {
        errors.push(this.$t('Form.InputErrors.Required'))
      } else if (!this.$v.company.ContactEmail.email) {
        errors.push(this.$t('Form.InputErrors.InvalidEmail'))
      }
      return errors
    },
  },

  created() {
    this.company.LocationId = this.locationId

    this.getAllCountries()
  },

  methods: {
    getAllCountries() {
      countryProvider.methods
        .getAllCountries()
        .then((response) => {
          if (response.status === 200) {
            this.countries = response.data
          }
        })
        .catch((err) => { })
    },

    save() {
      let self = this
      this.errorMessage = ''
      if (!this.isSaving) {
        this.isSaving = true
        this.$v.$touch()
        if (this.$v.$invalid) {
          this.isSaving = false
          self.errorMessage = self.$t(
            'Components.Companies.CreateCompany.Message_NotAllFilledIn'
          )
        } else {
          companyProvider.methods
            .createCompany(this.company)
            .then((response) => {
              self.$emit('nextAction', response.data)
            })
            .catch(() => {
              self.isSaving = false
              self.errorMessage = self.$t(
                'Components.Companies.CreateCompany.Message_Error500'
              )
            })
        }
      }
    },
  },
}
</script>

<style lang="scss" scoped></style>
